/**
 * The quiet clock of a browser call (2026-10-21 Riley) — the one place that
 * decides "it went quiet, ask" and "it stayed quiet, hang up", kept pure so
 * it can be tested, and shared with the /collect page through
 * window.__emberCall.quietClock.
 *
 * What counts as activity: the connection opening, Ember's audio (its
 * playback level / mode changes), a finalised transcript of the caller's
 * words — and, since 2026-10-21, the caller's own voice as the vendor's VAD
 * hears it (a score at or above VAD_SPEECH). Before that the clock only saw
 * finalised transcripts, and the vendor emits one only when it takes the
 * turn: a person talking for two or three minutes with pauses produced no
 * transcript, read as silence, and was hung up on mid-sentence (prod
 * 2026-10-19, one caller cut off 22 times, 11 of them mid-utterance).
 *
 * At QUIET_PROMPT_MS of nothing the page asks ("Still there?"); a tap or any
 * voice resets the clock. At QUIET_LIMIT_MS it ends the call as before. Ember
 * speaking is never quiet, whatever the clock says.
 */
export const QUIET_PROMPT_MS = 150_000;
export const QUIET_LIMIT_MS = 180_000;
/** The vendor's VAD score at which the caller counts as speaking. */
export const VAD_SPEECH = 0.5;

export type QuietVerdict = 'live' | 'prompt' | 'hangup';

export type QuietClock = {
  /** Anything that is not silence: connection, Ember's audio, a transcript, a tap on the prompt. */
  activity: (at?: number) => void;
  /** A VAD frame. Returns true when it counted as the caller's voice. */
  vad: (score: number, at?: number) => boolean;
  /** What the call should do now. `speaking` = Ember's audio is flowing. */
  read: (speaking: boolean, at?: number) => QuietVerdict;
  /** Time since the caller's voice was last heard; null when it never was. */
  sinceVoiceMs: (at?: number) => number | null;
  /** Time since any activity. */
  idleMs: (at?: number) => number;
};

export type QuietClockOptions = {
  promptMs?: number;
  limitMs?: number;
  vadSpeech?: number;
  now?: () => number;
};

export function createQuietClock(options: QuietClockOptions = {}): QuietClock {
  const promptMs = options.promptMs ?? QUIET_PROMPT_MS;
  const limitMs = options.limitMs ?? QUIET_LIMIT_MS;
  const vadSpeech = options.vadSpeech ?? VAD_SPEECH;
  const now = options.now ?? (() => Date.now());
  let lastActivityAt = now();
  let lastVoiceAt: number | null = null;

  return {
    activity(at) {
      lastActivityAt = at ?? now();
    },
    vad(score, at) {
      if (!(score >= vadSpeech)) return false;
      const t = at ?? now();
      lastActivityAt = t;
      lastVoiceAt = t;
      return true;
    },
    read(speaking, at) {
      if (speaking) return 'live';
      const idle = (at ?? now()) - lastActivityAt;
      if (idle >= limitMs) return 'hangup';
      if (idle >= promptMs) return 'prompt';
      return 'live';
    },
    sinceVoiceMs(at) {
      return lastVoiceAt === null ? null : Math.max(0, (at ?? now()) - lastVoiceAt);
    },
    idleMs(at) {
      return Math.max(0, (at ?? now()) - lastActivityAt);
    },
  };
}
